/**
 * The rebuilt bulk send wizard: Document → Fields → Signers → Recipients →
 * Review. Both doors share the stepper; picking a template skips Fields,
 * because a template arrives with its fields already placed.
 *
 * Nothing is sent until the last step. Creating the batch only validates and
 * saves a draft, so the sender can work through a per-row report before
 * committing — which is what makes a 500-line list safe to send.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import CustomStepper from "components/CustomStepper";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import { CloseIcon as EditorCloseIcon } from "screens/SignRequest/components/EditorIcons";
import editorTokens from "screens/SignRequest/SignRequestEditor.tokens";
import viewpaths from "global/constants/ViewPathConstants";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
  fromPhoneParts,
  toPhoneParts,
} from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  setReduxSelectedParticipant,
  setReduxSignRequest,
} from "redux/appSlice";
import { RequestFile } from "models/interfaces";
import UploadDocuments from "screens/SignRequest/components/UploadDocuments";
import SignRequestEditor from "screens/SignRequest/components/SignRequestEditor";
import { fieldColor } from "screens/SignRequest/SignRequestHelper";
import { RootState } from "utils/store";
import { bulkShellStyles } from "./BulkSend.styles";
import tokens from "./BulkSend.tokens";
import {
  BULK_STEPS,
  BulkCreateResponse,
  BulkDeliveryChannel,
  BulkRoleAssignment,
  BulkRoleErrors,
  BulkRow,
  BulkBatchSettings,
  BulkRoleMode,
  BulkSecurityKind,
  defaultBatchSettings,
  validateBulkReview,
  validateBulkRoles,
} from "./BulkSendTypesAndValidation";
import {
  applyRolesToSignRequest,
  buildBatchDefinition,
  fixedSignerIndexes,
  checkTypedRows,
  generatedSubject,
  initialRoleAssignments,
  layoutSignature,
  normaliseSlots,
  rolesDifferFromTemplate,
  toSignerSlots,
  typedRowsFromParsed,
  varyingSlots,
} from "./BulkSendHelper";
import {
  createBulkBatch,
  fetchBulkTemplateSlots,
  createBulkBatchFromCsv,
  createBulkBatchFromList,
  createBulkTemplateFromDocument,
  sendBulkBatch,
} from "./BulkSendService";
import EmptyState from "global/components/EmptyState/EmptyState";
import { centeredPageGutterSx } from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import useBulkGates from "./useBulkGates";
import { PickedTemplate } from "./components/TemplatePicker";
import BulkRolesStep from "./components/BulkRolesStep";
import BulkRecipientsStep from "./components/BulkRecipientsStep";
import BulkReviewStep from "./components/BulkReviewStep";
import {
  handleIdvAwareError,
  isIdvSecurityKey,
} from "helpers/identityVerificationHelpers";
import BulkRolesRail from "./components/BulkRolesRail";
import {
  MobileWizardFooter,
  MobileWizardHeader,
} from "./components/MobileWizardChrome";
import { TypedCell, TypedRow, emptyRow } from "./components/TypedRowsGrid";
import { FileIcon, SendIcon } from "./components/BulkSendIcons";

const p = tokens.palette;
const t = tokens.header;

const STEP_DOCUMENT = 0;
const STEP_FIELDS = 1;
const STEP_SIGNERS = 2;
const STEP_RECIPIENTS = 3;
const STEP_REVIEW = 4;

/**
 * How a batch's rows reached the server, kept so the batch can be created
 * again with the settings the sender chooses afterwards.
 *
 * It has to be replayable rather than patchable: creating a batch is what
 * validates the rows and fixes the definition, and there is no call to change
 * that definition later — so a Review edit means creating the batch again.
 */
type RecipientSource =
  | { kind: "typed"; rows: BulkRow[] }
  | { kind: "csv"; file: File }
  | { kind: "list"; listId: string };

const BulkSendComponent = () => {
  useTitle(title.BULK_SEND);
  const history = useHistory();

  const dispatch = useAppDispatch();
  /**
   * The phone layout is a different chrome, not a different flow: same state,
   * same steps, same calls — a collapsed stepper and a footer action instead
   * of the desktop toolbar.
   */
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  /**
   * The menu item is gated, but a URL is still a way in — and every row here
   * becomes an ordinary envelope, so the flow answers to the same plan gate and
   * the same permissions a single send does.
   */
  const gates = useBulkGates();

  /* The active workspace: the one every envelope of the batch is created in, and counted against. */
  const workspaceId = useAppSelector(
    (state: RootState) => state.auth.activeWorkspaceId || undefined,
  );
  const senderName = useAppSelector(
    (state: RootState) => (state.auth as any)?.userName ?? "",
  );
  const userEmail = useAppSelector(
    (state: RootState) => (state.auth as any)?.email ?? "",
  );
  /** `UploadDocuments` and the editor both write straight to redux. */
  const signRequest = useAppSelector(
    (state: RootState) => state.app.signRequest,
  );
  const uploadedFiles = signRequest?.files;
  const documentName = uploadedFiles?.[0]?.unsignedDoc || "";

  /**
   * Start clean, and — importantly — with `isBulkSend` off. That flag encodes
   * the *old* bulk model (one anonymous "Signer", no switching), which fights
   * the role-based placement this flow needs: it renames the editor's rail to
   * "Signer", stops the rail from switching roles, and disables "Assigned to"
   * in the field properties. A stale `true` from the old wizard would follow
   * the user in here, so it is reset rather than assumed.
   */
  useEffect(() => {
    dispatch(setReduxSignRequest(undefined));
  }, [dispatch]);

  const [currentStep, setCurrentStep] = useState<number>(STEP_DOCUMENT);
  const [template, setTemplate] = useState<PickedTemplate | null>(null);
  const [assignments, setAssignments] = useState<BulkRoleAssignment[]>([]);
  /** Some role verifies identity, so a refusal's own message is the one to show. */
  const usesIdv = assignments.some((assignment) =>
    isIdvSecurityKey(assignment.security),
  );
  const [roleErrors, setRoleErrors] = useState<BulkRoleErrors>({});
  const [roleFormError, setRoleFormError] = useState<string | undefined>();

  const [report, setReport] = useState<BulkCreateResponse | null>(null);
  /**
   * Bumped whenever rows arrive from a file or a saved list. The recipients
   * step watches it to swap the dropzone for the grid holding those rows.
   */
  const [importedAt, setImportedAt] = useState<number>(0);
  const [recipientSource, setRecipientSource] =
    useState<RecipientSource | null>(null);
  /**
   * The definition the current report was created from. Review edits are
   * compared against it, so a batch nobody changed is never created twice.
   */
  const createdDefinitionRef = useRef<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  /**
   * The layout already turned into a hidden template. Leaving the editor again
   * with nothing changed reuses that template instead of making another one —
   * a sender who goes back to nudge a field should not leave a trail of them.
   */
  const sentLayoutRef = useRef<string | null>(null);

  /** Typed rows, for the handful of recipients nobody opens a spreadsheet for. */
  const [typedRows, setTypedRows] = useState<TypedRow[]>([]);
  /** Rows the sender chose to leave out; they stay visible but do not send. */
  const [skippedRows, setSkippedRows] = useState<number[]>([]);

  const [settings, setSettings] = useState<BulkBatchSettings>({
    batchName: "",
    emailSubject: "",
    emailMessage: "",
    ...defaultBatchSettings(),
  });

  const updateSettings = (next: Partial<BulkBatchSettings>) =>
    setSettings((current) => ({ ...current, ...next }));

  const steps = useMemo(
    () =>
      BULK_STEPS.map((label, index) => ({
        label,
        isActive: index === currentStep,
        isCompleted: index < currentStep,
      })),
    [currentStep],
  );

  /**
   * A template chosen from the upload screen's own "Choose Template" modal.
   * Its signer slots drive the Signers step, so they are derived here rather
   * than fetched again.
   */
  /**
   * A template is a starting point, not a fixed thing — the same as it is in
   * the create-request flow. Picking one loads it as the request being built,
   * so every step after it is editable: documents, field placement, roles,
   * delivery, security, the lot.
   *
   * The template itself is never touched. The batch's own changes go into a
   * hidden template made from this request, which is what `from-document`
   * produces on the way out of the editor.
   */
  const handlePickTemplate = useCallback(
    async (chosen: any) => {
      const templateId = String(chosen?.id ?? chosen?.autoGeneratedId ?? "");
      const name = chosen?.templateName || chosen?.name || "Your template";

      /* Shaped as `processTemplateData` shapes it for a single send. */
      const request = {
        ...chosen,
        templateId: templateId || null,
        parentId: 0,
        approval: chosen?.approval ?? {
          approvalNeeded: false,
          orderNeeded: false,
          reviewers: [],
        },
        files: (chosen?.files ?? []).map((file: any) => ({
          ...file,
          unsignedDoc: file.unsignedPdf || file.unsignedDoc,
          unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
          isUploading: false,
        })),
        signers: (chosen?.signers ?? []).map((signer: any) => ({
          ...signer,
          phone: fromPhoneParts(signer?.phone),
          ...(signer?.requestSecurity
            ? {
                requestSecurity: {
                  ...signer.requestSecurity,
                  authPhone: fromPhoneParts(signer.requestSecurity.authPhone),
                },
              }
            : {}),
        })),
      };
      delete (request as { path?: string }).path;
      dispatch(setReduxSignRequest(request));

      let slots = toSignerSlots(chosen);
      try {
        const resolved = await fetchBulkTemplateSlots(templateId);
        if (resolved?.signerSlots?.length) {
          slots = normaliseSlots(resolved.signerSlots);
        }
      } catch {
        // Fall back to the template's own signers: the step still works, and the
        // server re-checks every column at create anyway.
      }
      setTemplate({ id: templateId, name, slots, source: chosen });
      setAssignments(initialRoleAssignments(slots));
      setRoleErrors({});
      setRoleFormError(undefined);
      setSettings((current) => ({
        ...current,
        batchName: current.batchName || name,
      }));
      /* Into the editor, where the template's own fields are on the page. */
      setCurrentStep(STEP_FIELDS);
    },
    [dispatch],
  );

  const handleChangeMode = (signerIndex: number, mode: BulkRoleMode) => {
    setAssignments((current) =>
      current.map((assignment) =>
        assignment.slot.signerIndex === signerIndex
          ? { ...assignment, mode }
          : assignment,
      ),
    );
    setRoleFormError(undefined);
  };

  const handleToggleChannel = (
    signerIndex: number,
    channel: BulkDeliveryChannel,
  ) =>
    setAssignments((current) =>
      current.map((assignment) => {
        if (assignment.slot.signerIndex !== signerIndex) return assignment;
        const has = assignment.channels.includes(channel);
        const next = has
          ? assignment.channels.filter((existing) => existing !== channel)
          : [...assignment.channels, channel];
        // A role has to be reachable somehow, so the last channel stays on.
        return { ...assignment, channels: next.length ? next : [channel] };
      }),
    );

  const handleChangeSecurity = (
    signerIndex: number,
    security: BulkSecurityKind,
  ) =>
    setAssignments((current) =>
      current.map((assignment) =>
        assignment.slot.signerIndex === signerIndex
          ? {
              ...assignment,
              security,
              // Changing away from an access code drops the code with it.
              accessCode: security === "code" ? assignment.accessCode : "",
            }
          : assignment,
      ),
    );

  const handleChangeDelay = (
    signerIndex: number,
    field: "delayDays" | "delayHours",
    value: number,
  ) =>
    setAssignments((current) =>
      current.map((assignment) =>
        assignment.slot.signerIndex === signerIndex
          ? { ...assignment, [field]: Math.max(0, value) }
          : assignment,
      ),
    );

  const handleChangeRoleField = (
    signerIndex: number,
    field: "name" | "email" | "phone" | "accessCode",
    value: string,
  ) => {
    setAssignments((current) =>
      current.map((assignment) =>
        assignment.slot.signerIndex === signerIndex
          ? { ...assignment, [field]: value }
          : assignment,
      ),
    );
    setRoleErrors((current) => {
      if (!current[signerIndex]) return current;
      const next = { ...current };
      const row = { ...next[signerIndex] };
      /* The error for the code field is keyed `code`, not `accessCode`. */
      delete row[field === "accessCode" ? "code" : field];
      if (Object.keys(row).length === 0) delete next[signerIndex];
      else next[signerIndex] = row;
      return next;
    });
  };

  /**
   * Upload hands the file straight to the server, which validates every row and
   * saves a draft. A batch already exists after this, so leaving and coming
   * back does not mean re-uploading.
   */
  const handleUploadRecipients = async (file: File) => {
    if (!template) return;
    try {
      setIsSubmitting(true);
      const definition = buildBatchDefinition({
        templateId: template.id,
        workspaceId,
        settings,
        assignments,
      });
      const response = await createBulkBatchFromCsv(file, definition);
      /* Older server: no parsed rows, so the read-only report stands. */
      if (!adoptParsedRows(response)) {
        rememberCreate({ kind: "csv", file }, definition, response);
      }
    } catch (error: any) {
      handleIdvAwareError(error, usesIdv);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * The subject starts as the documents' own, the way a single send's does,
   * and stops tracking them the moment the sender edits it.
   */
  const generatedRef = useRef<string>("");
  useEffect(() => {
    const names = (uploadedFiles ?? [])
      .map((file: RequestFile) => file.unsignedDoc)
      .filter(Boolean) as string[];
    const next = generatedSubject(
      names.length ? names : template?.name ? [template.name] : [],
    );
    if (!next) return;
    setSettings((current) =>
      current.emailSubject && current.emailSubject !== generatedRef.current
        ? current
        : { ...current, emailSubject: next },
    );
    generatedRef.current = next;
  }, [uploadedFiles, template]);

  /** Seed one blank row so the grid has something to type into. */
  useEffect(() => {
    if (currentStep !== STEP_RECIPIENTS) return;
    if (typedRows.length > 0) return;
    const slots = varyingSlots(assignments);
    if (slots.length === 0) return;
    setTypedRows([emptyRow(slots)]);
  }, [currentStep, typedRows.length, assignments]);

  /**
   * The single source every part of step 4 reads — the table's inline marks,
   * the panel's banner, chips, worklist and footer, and the rows actually
   * sent. Deriving it once is what stops them disagreeing.
   */
  const typedSummary = useMemo(
    () => checkTypedRows(typedRows, varyingSlots(assignments), skippedRows),
    [typedRows, assignments, skippedRows],
  );

  /**
   * The first row that will send, for the email preview. Only typed rows exist
   * in the browser — a CSV is parsed server-side, so there is nothing to
   * preview from until the rows come back.
   */
  const previewRow = useMemo(() => {
    const ready = typedSummary.checks.find(
      (check) => check.verdict === "ready",
    );
    if (!ready) return undefined;
    const row = typedRows[ready.index];
    const slots = varyingSlots(assignments);
    const first = slots[0] ? row?.[slots[0].signerIndex] : undefined;
    if (!first) return undefined;

    return {
      name: first.name,
      email: first.email,
      phone: first.phone || undefined,
      values: { name: first.name, email: first.email },
    };
  }, [typedSummary, typedRows, assignments]);

  const handleTypedSkip = (rowIndex: number) => {
    setSkippedRows((current) =>
      current.includes(rowIndex) ? current : [...current, rowIndex],
    );
    /*
      The batch the server checked still holds this row. Dropping the report
      forces it to be built again without it — otherwise "skip" would hide the
      row on screen while it still went out.
    */
    setReport(null);
  };

  const handleTypedChange = (
    rowIndex: number,
    signerIndex: number,
    cell: TypedCell,
  ) => {
    setTypedRows((current) =>
      current.map((row, index) =>
        index === rowIndex ? { ...row, [signerIndex]: cell } : row,
      ),
    );
    /*
      The server's report describes the rows as they were when it was made.
      Editing one makes it stale, so it is dropped and the list is re-checked
      rather than letting the sender advance on an answer about old rows.
    */
    setReport(null);
    // Typing in a skipped row means the sender is dealing with it after all.
    setSkippedRows((current) => current.filter((index) => index !== rowIndex));
  };

  const handleTypedAdd = () =>
    setTypedRows((current) => [
      ...current,
      emptyRow(varyingSlots(assignments)),
    ]);

  const handleTypedRemove = (rowIndex: number) => {
    setTypedRows((current) => current.filter((_, index) => index !== rowIndex));
    setSkippedRows((current) =>
      current
        .filter((index) => index !== rowIndex)
        .map((index) => (index > rowIndex ? index - 1 : index)),
    );
  };

  /**
   * Same create call the CSV takes, so typed rows get the same per-row report.
   * Untouched rows are dropped rather than sent as blanks the server would
   * only reject.
   */
  const handleTypedSubmit = async () => {
    if (!template) return;
    const slots = varyingSlots(assignments);
    const readyIndexes = typedSummary.checks
      .filter((check) => check.verdict === "ready")
      .map((check) => check.index);
    const rows = typedRows
      .map((row, index) => ({ row, index }))
      .filter(({ index }) => readyIndexes.includes(index))
      .map(({ row, index }) => ({
        /* The grid's own row number, so a server issue names the row on screen. */
        rowNumber: index + 1,
        recipients: slots.map((slot) => {
          const cell = row[slot.signerIndex];
          return {
            signerIndex: slot.signerIndex,
            name: (cell?.name ?? "").trim(),
            email: (cell?.email ?? "").trim(),
            /*
              Keyed off the slot's live delivery, not the template's own flag:
              a role switched to Text on step 3 collects a phone in the table,
              and it has to reach the payload too.
            */
            ...(toPhoneParts(cell?.phone ?? "")
              ? { phone: toPhoneParts(cell.phone) }
              : {}),
            /* One code per recipient — the row's own, never the batch's. */
            ...(slot.security === "code" && cell?.code?.trim()
              ? { accessCode: cell.code.trim() }
              : {}),
          };
        }),
      }));

    try {
      setIsSubmitting(true);
      const baseDefinition = buildBatchDefinition({
        templateId: template.id,
        workspaceId,
        settings,
        assignments,
      });
      const definition = { ...baseDefinition, rows };
      const response = await createBulkBatch(definition);
      rememberCreate({ kind: "typed", rows }, baseDefinition, response);
      const serverFlagged = (response.issues ?? []).some(
        (issue) => issue.severity === "error",
      );
      if (!serverFlagged) setCurrentStep(STEP_REVIEW);
    } catch (error: any) {
      handleIdvAwareError(error, usesIdv);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** A saved list runs the same create call, so it gets the same report. */
  const handleUseSavedList = async (listId: string, listName: string) => {
    if (!template) return;
    try {
      setIsSubmitting(true);
      const definition = buildBatchDefinition({
        templateId: template.id,
        workspaceId,
        settings,
        assignments,
      });
      const response = await createBulkBatchFromList(listId, definition);
      if (!adoptParsedRows(response)) {
        rememberCreate({ kind: "list", listId }, definition, response);
      }
    } catch (error: any) {
      handleIdvAwareError(error, usesIdv);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** The batch definition as it stands right now. */
  const currentDefinition = () =>
    buildBatchDefinition({
      templateId: template?.id ?? "",
      workspaceId,
      settings,
      assignments,
    });

  /** Create the batch from whichever route its rows came in by. */
  const createBatchFrom = (
    source: RecipientSource,
    definition: ReturnType<typeof currentDefinition>,
  ): Promise<BulkCreateResponse> => {
    if (source.kind === "csv") {
      return createBulkBatchFromCsv(source.file, definition);
    }
    if (source.kind === "list") {
      return createBulkBatchFromList(source.listId, definition);
    }
    return createBulkBatch({ ...definition, rows: source.rows });
  };

  /**
   * Rows the server parsed, loaded into the grid for editing.
   *
   * An upload used to end at a read-only report: a bad address meant going
   * back to the file and starting over, and a column the file never had — an
   * access code — could not be supplied at all. When the server sends back
   * what it read, those rows become editable here instead, and re-submitting
   * goes out through the same call the typed route uses.
   */
  const adoptParsedRows = (response: BulkCreateResponse): boolean => {
    if (!response?.rows?.length) return false;
    setTypedRows(typedRowsFromParsed(response.rows, varyingSlots(assignments)));
    setSkippedRows([]);
    /* The grid re-checks every row itself, so the server's report would only
       be a second opinion about rows the sender is already editing. */
    setReport(null);
    setImportedAt(Date.now());
    return true;
  };

  /** Record what the batch was created from, so Review edits can replay it. */
  const rememberCreate = (
    source: RecipientSource,
    definition: ReturnType<typeof currentDefinition>,
    response: BulkCreateResponse,
  ) => {
    setRecipientSource(source);
    createdDefinitionRef.current = JSON.stringify(definition);
    setReport(response);
  };

  const handleSend = async () => {
    if (!report?.batchId) return;
    const formError = validateBulkReview(
      settings.batchName,
      settings.emailSubject,
      settings.emailMessage,
    );
    if (formError) {
      openErrorNotification(formError);
      return;
    }
    /* One envelope per valid row; the server refuses the same batch at send. */
    const envelopesLeft = report.workspaceEnvelopesLeft;
    if (typeof envelopesLeft === "number" && report.validRows > envelopesLeft) {
      openErrorNotification(
        envelopesLeft <= 0
          ? "This workspace has no envelopes left."
          : `Only ${envelopesLeft} envelope${envelopesLeft === 1 ? "" : "s"} left in this workspace.`,
      );
      return;
    }
    try {
      setIsSending(true);
      /*
        Everything on this step — subject, message, reminders, expiry,
        permissions, schedule, batch name — was fixed when the batch was
        created, one step earlier. There is no call to change it, so a batch
        the sender has since edited is created again before it goes out.
      */
      let batchId = report.batchId;
      const definition = currentDefinition();
      if (
        recipientSource &&
        JSON.stringify(definition) !== createdDefinitionRef.current
      ) {
        const recreated = await createBatchFrom(recipientSource, definition);
        if (!recreated?.batchId) {
          openErrorNotification(
            "The batch could not be saved with your latest settings.",
          );
          return;
        }
        rememberCreate(recipientSource, definition, recreated);
        batchId = recreated.batchId;
      }
      const response = await sendBulkBatch(batchId);
      openSuccessNotification(
        `Queued ${response?.queuedRows ?? report.validRows} requests. They'll go out over the next few minutes.`,
      );
      history.push(`${viewpaths.bulkBatchViewPath}/${batchId}`);
    } catch (error: any) {
      handleIdvAwareError(error, usesIdv);
    } finally {
      setIsSending(false);
    }
  };

  const canLeaveStep = (step: number): boolean => {
    if (step === STEP_DOCUMENT) {
      if (template) return true;
      const hasFile = (uploadedFiles?.length ?? 0) > 0;
      if (!hasFile) {
        openErrorNotification(
          "Upload a document, or choose a template, to start.",
        );
      }
      return hasFile;
    }
    if (step === STEP_SIGNERS) {
      const result = validateBulkRoles(assignments);
      setRoleErrors(result.errors);
      setRoleFormError(result.formError);
      if (!result.isValid && result.formError) {
        openErrorNotification(result.formError);
      }
      return result.isValid;
    }
    if (step === STEP_FIELDS) {
      // A role with no fields can still sign, so the bar is one placed field
      // on the document — not one per role.
      const hasField = (signRequest?.files ?? []).some(
        (file: RequestFile) => (file.fields?.length ?? 0) > 0,
      );
      if (!hasField) {
        openErrorNotification(
          "Place at least one field before continuing — a signature, usually.",
        );
      }
      return hasField;
    }
    if (step === STEP_RECIPIENTS) return Boolean(report);
    return true;
  };

  /**
   * The editor needs a role to place fields for. Seeding one here rather than
   * letting `FieldsPanel` auto-create a blank signer keeps the id 0-based, so
   * the rail's colour and the field's colour agree.
   */
  useEffect(() => {
    if (currentStep !== STEP_FIELDS) return;
    if ((signRequest?.signers?.length ?? 0) > 0) return;
    const firstRole = {
      id: 0,
      name: "",
      email: "",
      requestRole: "Signer 1",
      order: 1,
      color: fieldColor[0],
      cc: false,
    };
    dispatch(setReduxSignRequest({ signers: [firstRole] }));
    dispatch(setReduxSelectedParticipant(firstRole));
  }, [currentStep, signRequest?.signers?.length, dispatch]);

  const goToStep = (step: number) => {
    /*
      The stepper is a shortcut through `handleNext`, never around it: moving
      forward has to do the same work, or the roles the sender just set never
      reach the batch. Only going back is a plain jump.
    */
    if (step > currentStep) {
      if (step !== currentStep + 1) return;
      handleNext();
      return;
    }
    if (step > currentStep && !canLeaveStep(currentStep)) return;
    setCurrentStep(step);
  };

  /**
   * Leaving the editor on the upload path turns the laid-out document into the
   * hidden template a batch is built from, and hands back the signer slots the
   * next step needs. The template never appears in the template list and does
   * not count against the plan's template limit, so the sender never sees the
   * word "template" on this route.
   */
  const handleUploadedDocumentReady = async () => {
    const signature = layoutSignature(signRequest);
    if (template && sentLayoutRef.current === signature) {
      setCurrentStep(STEP_SIGNERS);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await createBulkTemplateFromDocument(signRequest);
      /*
        The server labels a slot "Signer 0" when it has no name of its own, but
        the sender just named these roles in the editor — so their words win,
        and the server's fallback only fills a role left blank.
      */
      const named = toSignerSlots(signRequest);
      const slots =
        response?.signerSlots?.length > 0
          ? normaliseSlots(
              response.signerSlots.map((slot) => ({
                ...slot,
                label:
                  named.find((role) => role.signerIndex === slot.signerIndex)
                    ?.label || slot.label,
              })),
            )
          : named;
      /*
        The server labels the hidden template with its own id, which is no use
        to a person scanning the Batches list — the document's own name is what
        they recognise, so it wins and the server's label is the fallback.
      */
      const name = documentName || response?.name || "Your document";
      setTemplate({
        id: response.templateId,
        name,
        slots,
        source: signRequest,
      });
      setAssignments(initialRoleAssignments(slots));
      setSettings((current) => ({
        ...current,
        batchName: current.batchName || name,
      }));
      sentLayoutRef.current = signature;
      setCurrentStep(STEP_SIGNERS);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Delivery and security edited on the Signers step belong to this batch, not
   * to the template it came from — so a changed role is written onto a copy of
   * the request and `from-document` turns that into the batch's own hidden
   * template. The source template is never touched, and a send that changed
   * nothing reuses the template it already had.
   */
  const handleRolesReady = async () => {
    if (!template || !rolesDifferFromTemplate(assignments)) {
      setCurrentStep(STEP_RECIPIENTS);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await createBulkTemplateFromDocument(
        applyRolesToSignRequest(template.source, assignments),
      );
      const slots =
        response?.signerSlots?.length > 0
          ? response.signerSlots.map((slot) => ({
              ...slot,
              label:
                assignments.find(
                  (role) => role.slot.signerIndex === slot.signerIndex,
                )?.slot.label || slot.label,
            }))
          : assignments.map((role) => role.slot);

      setTemplate({ ...template, id: response.templateId, slots });
      setAssignments((current) =>
        current.map((assignment) => ({
          ...assignment,
          slot:
            slots.find(
              (slot) => slot.signerIndex === assignment.slot.signerIndex,
            ) ?? assignment.slot,
        })),
      );
      setCurrentStep(STEP_RECIPIENTS);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    /*
      The recipient list is checked by the server before it can be left, and
      that check is what produces the report `canLeaveStep` waits for — so
      submitting has to happen before the guard, not after it.
    */
    if (currentStep === STEP_RECIPIENTS && !report && typedRows.length > 0) {
      handleTypedSubmit();
      return;
    }
    if (!canLeaveStep(currentStep)) return;
    if (currentStep === STEP_FIELDS) {
      handleUploadedDocumentReady();
      return;
    }
    if (currentStep === STEP_SIGNERS) {
      handleRolesReady();
      return;
    }
    setCurrentStep(Math.min(currentStep + 1, STEP_REVIEW));
  };

  const handlePrevious = () =>
    setCurrentStep(Math.max(currentStep - 1, STEP_DOCUMENT));

  const renderStep = () => {
    switch (currentStep) {
      case STEP_DOCUMENT:
        return <UploadDocuments bulk onTemplateSelect={handlePickTemplate} />;

      case STEP_SIGNERS:
        return (
          <BulkRolesStep
            assignments={assignments}
            errors={roleErrors}
            formError={roleFormError}
            recipientCount={report?.validRows ?? 0}
            onChangeMode={handleChangeMode}
            onChangeField={handleChangeRoleField}
            onToggleChannel={handleToggleChannel}
            onChangeSecurity={handleChangeSecurity}
            onChangeDelay={handleChangeDelay}
          />
        );

      case STEP_RECIPIENTS:
        return template ? (
          <BulkRecipientsStep
            templateName={template.name}
            templateId={template.id}
            varyingSlots={varyingSlots(assignments)}
            fixedSignerIndexes={fixedSignerIndexes(assignments)}
            report={report}
            isSubmitting={isSubmitting}
            typedRows={typedRows}
            typedSummary={typedSummary}
            importedAt={importedAt}
            onTypedChange={handleTypedChange}
            onTypedAdd={handleTypedAdd}
            onTypedRemove={handleTypedRemove}
            onTypedSkip={handleTypedSkip}
            onUseSavedList={handleUseSavedList}
            onUpload={handleUploadRecipients}
          />
        ) : null;

      case STEP_REVIEW:
        return template ? (
          <BulkReviewStep
            template={template.source}
            assignments={assignments}
            validRows={report?.validRows ?? 0}
            settings={settings}
            previewRow={previewRow}
            senderName={senderName}
            senderEmail={userEmail}
            onChange={updateSettings}
          />
        ) : null;

      case STEP_FIELDS:
        return (
          <SignRequestEditor
            setLoader={setIsSubmitting}
            rolesRail={<BulkRolesRail />}
            onAddDocuments={() => setCurrentStep(STEP_DOCUMENT)}
          />
        );

      default:
        return null;
    }
  };

  if (!gates.bulkSend.available || !gates.canCreate) {
    return (
      <Box sx={centeredPageGutterSx}>
        <EmptyState
          size="full"
          icon="doc"
          title={
            gates.canCreate
              ? "Bulk send isn't on your plan"
              : "You don't have permission to send requests"
          }
          body={
            gates.canCreate
              ? gates.bulkSend.tooltip ||
                "Send one document to many people, each getting their own copy."
              : "Ask an administrator for permission to create and send documents."
          }
          primaryAction={{
            label: "Back to sent",
            onClick: () => history.push(viewpaths.sentSignRequestsViewPath),
          }}
        />
      </Box>
    );
  }

  /*
    The send lives in the wizard's own toolbar, beside Previous, where the
    create-request flow keeps its final action — not inside the step.
  */
  const validRows = report?.validRows ?? 0;
  const smsCreditsRequired = report?.smsCreditsRequired;
  const smsCreditBalance = report?.smsCreditBalance;
  const isShortOfCredits =
    typeof smsCreditsRequired === "number" &&
    typeof smsCreditBalance === "number" &&
    smsCreditBalance < smsCreditsRequired;
  const sendBlocked =
    isSending ||
    validRows === 0 ||
    isShortOfCredits ||
    !gates.canSend ||
    !gates.sendEmail.available;
  const sendLabel = isSending
    ? "Sending…"
    : settings.scheduleTime
      ? `Schedule ${validRows}`
      : `Send ${validRows}`;

  const primaryLabel =
    currentStep === STEP_REVIEW
      ? isSending
        ? "Sending…"
        : `Send ${report?.validRows ?? 0}`
      : "Continue";

  if (isPhone) {
    return (
      <Box sx={bulkShellStyles.root}>
        <MobileWizardHeader
          currentStep={currentStep}
          onBack={currentStep > STEP_DOCUMENT ? handlePrevious : undefined}
          onClose={() => history.push(viewpaths.sentSignRequestsViewPath)}
        />
        <Box sx={bulkShellStyles.body}>{renderStep()}</Box>
        {
          <MobileWizardFooter
            primaryLabel={primaryLabel}
            primaryDisabled={
              isSubmitting ||
              /* The same gates the toolbar applies: a phone is not a bypass. */
              (currentStep === STEP_REVIEW && sendBlocked)
            }
            onPrimary={currentStep === STEP_REVIEW ? handleSend : handleNext}
            secondaryLabel={currentStep > STEP_DOCUMENT ? "Back" : undefined}
            onSecondary={
              currentStep > STEP_DOCUMENT ? handlePrevious : undefined
            }
          />
        }
      </Box>
    );
  }

  return (
    <Box sx={bulkShellStyles.root}>
      <Box component="header" sx={bulkShellStyles.header}>
        <Tooltip title="Close">
          <IconButton
            aria-label="Close bulk send"
            onClick={() => history.push(viewpaths.sentSignRequestsViewPath)}
            sx={{
              width: editorTokens.header.closeSize,
              height: editorTokens.header.closeSize,
              borderRadius: editorTokens.header.closeRadius,
              color: editorTokens.header.closeColor,
            }}
          >
            <EditorCloseIcon size={editorTokens.header.closeIconSize} />
          </IconButton>
        </Tooltip>

        <Box sx={bulkShellStyles.badge}>
          <SendIcon size={t.pillIconSize} color={p.accent} />
          BULK SEND
        </Box>

        <CustomStepper
          steps={steps}
          currentStep={currentStep}
          handleChangeStep={goToStep}
        />

        <Box sx={bulkShellStyles.headerActions}>
          {currentStep > STEP_DOCUMENT && (
            <Button
              variant="outlined"
              onClick={handlePrevious}
              sx={{
                height: t.buttonHeight,
                borderRadius: t.buttonRadius,
                fontSize: t.buttonFontSize,
                px: t.previousPaddingX,
              }}
            >
              Previous
            </Button>
          )}
          {currentStep < STEP_REVIEW ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={15} color="inherit" thickness={5} />
                ) : undefined
              }
              sx={{
                height: t.buttonHeight,
                borderRadius: t.buttonRadius,
                fontSize: t.buttonFontSize,
                px: t.nextPaddingX,
              }}
            >
              {isSubmitting ? "Checking…" : "Next"}
            </Button>
          ) : (
            <Tooltip
              describeChild
              title={
                !gates.canSend
                  ? "You don't have permission to send requests."
                  : gates.sendEmail.tooltip
              }
              disableHoverListener={gates.canSend && gates.sendEmail.available}
            >
              <Box component="span" sx={{ display: "inline-flex" }}>
                <CustomDropdownButton
                  buttonLabel={sendLabel}
                  startIcon={
                    isSending ? (
                      <CircularProgress
                        size={15}
                        color="inherit"
                        thickness={5}
                      />
                    ) : (
                      <SendIcon size={t.pillIconSize} />
                    )
                  }
                  disabled={sendBlocked}
                  handleMainClick={handleSend}
                  menuItems={[
                    {
                      icon: <FileIcon size={t.pillIconSize} />,
                      label: "Save as draft",
                      disabled: isSending,
                      onClick: () =>
                        history.push(
                          `${viewpaths.bulkBatchViewPath}/${
                            report?.batchId ?? ""
                          }`,
                        ),
                    },
                  ]}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={bulkShellStyles.body}>{renderStep()}</Box>
    </Box>
  );
};

export default BulkSendComponent;
